import React, { useEffect, useState } from "react";
import { useRef } from "react";
import styled from "styled-components";
import { Toast, ImageCropModal, PortalFormLabel, portalColors } from "src/components";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";

const UploadPreviewRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1.25rem;
    margin-bottom: 1rem;
`;

const UploadDropzone = styled.div`
    width: min(100%, 20rem);
    aspect-ratio: 16 / 9;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${portalColors.surfaceSoft};
    cursor: pointer;
    overflow: hidden;
`;

const PreviewImage = styled.img`
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: center;
`;

const EmptyUploadPrompt = styled.p`
    margin: 0;
    font-size: 28px;
    color: #888;
`;

const RemoveImageButton = styled.button`
    padding: 5px 10px;
    background: white;
    border: 1px solid ${portalColors.border};
    border-radius: 14px;
    cursor: pointer;
    box-shadow: 0px 5px 5px #0000001A;
    width: 139px;
    height: 40px;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: 0;
`;

const ImageUpload = (props) => {
    const { setFormImage, setImageChanged, label = "Upload Poll Image (Optional)" } = props;
    const [image, setImage] = useState(props.image ? props.image : null);

    useEffect(() => {
        if (!props.image || typeof props.image === "string") {
            setImage(props.image ? props.image : null);
        }
    }, [props.image]);

    // Crop Image
    const [imageToCrop, setImageToCrop] = useState(null);
    const [showCropperModal, setShowCropperModal] = useState(false);
            
    const handleCropComplete = (file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        if (image && image.startsWith("blob:")) {
            URL.revokeObjectURL(image);
        }

        setImage(url); // For preview
        setShowCropperModal(false);
        setFormImage(file); 
        setImageChanged(true);
    };

    const fileInputRef = useRef(null);

    const handleFiles = (files) => {
        const validImageTypes = ["image/jpeg", "image/png", "image/gif"];
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();
            reader.onload = () => {
                if (validImageTypes.includes(file.type)) {
                    setImage(reader.result);
                    setImageToCrop(reader.result);
                    setShowCropperModal(true);
                } else {
                    Toast.error("Invalid file type. Please select an image or video.");
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDropzoneClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        handleFiles(event.dataTransfer.files);
    };

    const handleInputChange = (event) => {
        handleFiles(event.target.files);
    };

    const removeImage = () => {
        setImage(null);
        setFormImage(null);
        setImageChanged(true);
    };

    return (
        <>
            <PortalFormLabel>
                {label}
            </PortalFormLabel>
            <UploadPreviewRow>
                <UploadDropzone
                    onClick={handleDropzoneClick}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={handleInputChange}
                        style={{ display: "none" }}
                    />
                    {image ? (
                        <PreviewImage src={image} alt="Preview" />
                    ) : (
                        <EmptyUploadPrompt>+</EmptyUploadPrompt>
                    )}
                </UploadDropzone>
                {image && (
                    <RemoveImageButton type="button" onClick={removeImage}>
                        Remove Image
                    </RemoveImageButton>
                )}
            </UploadPreviewRow>
            <ImageCropModal
                imageSrc={imageToCrop}
                isOpen={showCropperModal}
                onClose={(shouldClear) => {
                    setShowCropperModal(false);
                    if (shouldClear) {
                        setImageToCrop(null);
                        setImage(null);
                    }
                }}
                onCropComplete={handleCropComplete}
                aspectRatios={[
                    { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN },
                ]}
                allowedOrientations={["landscape"]}
                lockedAspectRatio={undefined}
            />
        </>
    );
};

export default ImageUpload;
